// Must run before App (and createBrowserRouter) so history initializes clean.
import './lib/normalize-gcs-index-path'

import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import App from './App'
import './index.css'

// Expose the active product to the design system. This lets shared primitives
// keep neutral defaults while Embrace can opt into its own visual language
// without restyling Console, Portal, Coach, or the marketing site.
document.documentElement.setAttribute('data-site', import.meta.env.VITE_SITE || 'embrace')

// Initialize analytics in production only.
// Wrapped in try-catch so a failing tracker can never prevent React from mounting.
if (import.meta.env.PROD) {
  try {
    // Microsoft Clarity (production only). The Clarity project id is hardcoded,
    // so — like Apollo — guard on VITE_ENV, not import.meta.env.PROD (which is
    // true for staging builds too) to keep staging traffic out of the workspace.
    if (import.meta.env.VITE_ENV === 'production') {
      void import('@microsoft/clarity')
        .then(({ default: Clarity }) => {
          Clarity.init('ux9z696rtw')
          Clarity.setTag('site', import.meta.env.VITE_SITE || 'embrace')
        })
        .catch(() => {
          // Analytics must never affect the application experience.
        })
    }

    // Google Analytics - using Google's exact recommended implementation
    const gaId = import.meta.env.VITE_GA_MEASUREMENT_ID
    if (gaId) {
      const script = document.createElement('script')
      script.async = true
      script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`
      document.head.appendChild(script)

      window.dataLayer = window.dataLayer || []
      // biome-ignore lint/suspicious/noExplicitAny: GA requires this exact signature
      function gtag(..._args: any[]) {
        // biome-ignore lint/style/noArguments: GA requires 'arguments' object, not rest params
        window.dataLayer.push(arguments)
      }
      window.gtag = gtag
      gtag('js', new Date())
      gtag('config', gaId)
    }

    // LinkedIn Insight Tag
    const linkedInPartnerId = import.meta.env.VITE_LINKEDIN_PARTNER_ID
    if (linkedInPartnerId) {
      window._linkedin_partner_id = linkedInPartnerId
      window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || []
      window._linkedin_data_partner_ids.push(linkedInPartnerId)

      const liScript = document.createElement('script')
      liScript.async = true
      liScript.src = 'https://snap.licdn.com/li.lms-analytics/insight.min.js'
      document.head.appendChild(liScript)
    }

    // Apollo.io website tracker (pelgo marketing site, production only).
    // Guard on VITE_ENV, not import.meta.env.PROD, which is also true for
    // staging builds (vite build sets it regardless of --mode).
    if (import.meta.env.VITE_SITE === 'pelgo' && import.meta.env.VITE_ENV === 'production') {
      const apolloCacheBuster = Math.random().toString(36).substring(7)
      const apolloScript = document.createElement('script')
      apolloScript.src = `https://assets.apollo.io/micro/website-tracker/tracker.iife.js?nocache=${apolloCacheBuster}`
      apolloScript.async = true
      apolloScript.defer = true
      apolloScript.onload = () => {
        window.trackingFunctions?.onLoad({ appId: '6a35a0c2e94512001c8720bd' })
      }
      document.head.appendChild(apolloScript)
    }
  } catch (_) {
    // Analytics must never block the app from rendering
  }
}

const rootElement = document.getElementById('root')

if (!rootElement) {
  throw new Error('Root element not found')
}

createRoot(rootElement).render(
  <StrictMode>
    <App />
  </StrictMode>,
)
